import React, { useRef, useEffect, useCallback, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { cloneDeep, Template, checkTemplate, Lang, isBlankPdf } from "@pdfme/common";
import { Designer } from "@pdfme/ui";
import { Schema } from "@pdfme/common";
import {
  getFontsData,
  getTemplateById,
  getBlankTemplate,
  readFile,
  handleLoadTemplate,
  generatePDF,
  downloadJsonFile,
  translations,
  createSampleMultiPageTemplate,
} from "../helper";
import { getPlugins } from '../plugins';

// Types
interface NavItem {
  label: string;
  content: React.ReactNode;
}

interface EnhancedNavBarProps {
  items: NavItem[];
}

// Enhanced NavBar Component
const EnhancedNavBar: React.FC<EnhancedNavBarProps> = ({ items }) => {
  return (
    <div className="w-full bg-gray-50 border-b border-gray-200 shadow-sm">
      <div className="flex flex-wrap items-center justify-start px-4 py-2 gap-4">
        {items.map((item, index) => (
          <div 
            key={index} 
            className={`flex items-center gap-2 ${
              item.label.includes('Headers & Footers') || item.label.includes('Editing Headers/Footers')
                ? 'bg-white border border-gray-300 rounded-lg px-3 py-2 shadow-sm' 
                : item.label === 'Pages'
                ? 'bg-blue-50 border border-blue-200 rounded-lg px-3 py-2' 
                : item.label === 'PDF & Template'
                ? 'bg-purple-50 border border-purple-200 rounded-lg px-3 py-2'
                : item.label.includes('Actions')
                ? 'bg-white border border-gray-200 rounded-lg px-2 py-2 shadow-sm'
                : 'min-w-0'
            }`}
          >
            {item.label && (
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                {item.label}:
              </label>
            )}
            <div className="flex items-center gap-1 min-w-0">
              {item.content}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

function DesignerApp() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const designerRef = useRef<HTMLDivElement | null>(null);
  const designer = useRef<Designer | null>(null);

  const [editingStaticSchemas, setEditingStaticSchemas] = useState<boolean>(false);
  const [originalTemplate, setOriginalTemplate] = useState<Template | null>(null);
  const [totalPages, setTotalPages] = useState<number>(1);

  // Initialize Designer
  const buildDesigner = useCallback(async () => {
    if (!designerRef.current) return;
    
    try {
      let template = getBlankTemplate();
      const templateIdFromQuery = searchParams.get("template");
      searchParams.delete("template");
      setSearchParams(searchParams, { replace: true });
      const templateFromLocal = localStorage.getItem("template");

      if (templateIdFromQuery) {
        const templateJson = await getTemplateById(templateIdFromQuery);
        checkTemplate(templateJson);
        template = templateJson;
        if (!templateFromLocal) {
          localStorage.setItem("template", JSON.stringify(templateJson));
        }
      } else if (templateFromLocal) {
        const templateJson = JSON.parse(templateFromLocal);
        checkTemplate(templateJson);
        template = templateJson;
      }

      designer.current = new Designer({
        domContainer: designerRef.current,
        template,
        options: {
          font: getFontsData(),
          lang: 'en',
          labels: { 'signature.clear': "🗑️" },
          theme: { token: { colorPrimary: "#25c2a0" } },
          icons: {
            multiVariableText: '<svg fill="#000000" width="24px" height="24px" viewBox="0 0 24 24"><path d="M6.643,13.072,17.414,2.3a1.027,1.027,0,0,1,1.452,0L20.7,4.134a1.027,1.027,0,0,1,0,1.452L9.928,16.357,5,18ZM21,20H3a1,1,0,0,0,0,2H21a1,1,0,0-2Z"/></svg>',
          },
          maxZoom: 250,
        },
        plugins: getPlugins(),
      });
      
      setTotalPages(template.schemas.length);
      designer.current.onSaveTemplate(onSaveTemplate);

    } catch (error) {
      localStorage.removeItem("template");
      console.error(error);
    }
  }, [searchParams, setSearchParams]);

  // Helper function to ensure header/footer fields are properly merged
  const ensureHeaderFooterFieldsAreMerged = () => {
    if (!designer.current) return null;
    
    const currentTemplate = designer.current.getTemplate();
    
    if (editingStaticSchemas && originalTemplate) {
      // We're in editing mode, need to merge back to original template
      const editedTemplate = cloneDeep(currentTemplate);
      const merged = cloneDeep(originalTemplate);
      
      if (isBlankPdf(merged.basePdf)) {
        merged.basePdf.staticSchema = editedTemplate.schemas[0];
      }
      
      return merged;
    }
    
    // Not in editing mode, return current template
    return currentTemplate;
  };

  // PDF Preview - FIXED
  const onPreviewPDF = () => {
    if (!designer.current) {
      toast.error("Designer not initialized");
      return;
    }

    // Get the properly merged template
    const templateToPreview = ensureHeaderFooterFieldsAreMerged();
    if (!templateToPreview) {
      toast.error("Failed to prepare template for preview");
      return;
    }

    const hasContent = templateToPreview.schemas.some(schema => schema.length > 0);
    const hasHeaderFooter = isBlankPdf(templateToPreview.basePdf) && 
                           templateToPreview.basePdf.staticSchema && 
                           templateToPreview.basePdf.staticSchema.length > 0;
    
    if (!hasContent && !hasHeaderFooter) {
      toast.error("Cannot preview empty template. Please add some fields or header/footer elements first.");
      return;
    }

    localStorage.setItem("previewTemplate", JSON.stringify(templateToPreview));
    navigate("/preview-pdf");
    toast.success("Navigating to PDF preview...");
  };

  // Page Management Functions
  const addNewPage = () => {
    if (designer.current) {
      const currentTemplate = designer.current.getTemplate();
      const newTemplate = cloneDeep(currentTemplate);
      newTemplate.schemas.push([]);
      designer.current.updateTemplate(newTemplate);
      setTotalPages(newTemplate.schemas.length);
      toast.success(`Added page ${newTemplate.schemas.length}`);
    }
  };

  const removePage = (pageIndex: number) => {
    if (designer.current) {
      const currentTemplate = designer.current.getTemplate();
      
      if (currentTemplate.schemas.length <= 1) {
        toast.error("Cannot remove the last page");
        return;
      }
      
      const newTemplate = cloneDeep(currentTemplate);
      newTemplate.schemas.splice(pageIndex, 1);
      designer.current.updateTemplate(newTemplate);
      setTotalPages(newTemplate.schemas.length);
      toast.success(`Removed page ${pageIndex + 1}`);
    }
  };

  const duplicatePage = (pageIndex: number) => {
    if (designer.current) {
      const currentTemplate = designer.current.getTemplate();
      const newTemplate = cloneDeep(currentTemplate);
      const pageToClone = cloneDeep(newTemplate.schemas[pageIndex]);
      
      const duplicatedPage = pageToClone.map((field) => ({
        ...field,
        name: `${field.name}_copy_${Date.now()}`
      }));
      
      newTemplate.schemas.splice(pageIndex + 1, 0, duplicatedPage);
      designer.current.updateTemplate(newTemplate);
      setTotalPages(newTemplate.schemas.length);
      toast.success(`Duplicated page ${pageIndex + 1}`);
    }
  };

  const loadSampleMultiPageTemplate = () => {
    if (designer.current) {
      const multiPageTemplate = createSampleMultiPageTemplate();
      designer.current.updateTemplate(multiPageTemplate);
      setTotalPages(multiPageTemplate.schemas.length);
      localStorage.setItem("template", JSON.stringify(multiPageTemplate));
      toast.success("Loaded sample multi-page template!");
    }
  };

  const loadTemplateToPage = (pageIndex: number, templateSchema: Schema[]) => {
  if (!designer.current) {
    toast.error("Designer not initialized");
    return;
  }

  const currentTemplate = designer.current.getTemplate();
  const newTemplate = cloneDeep(currentTemplate);
  
  // Ensure the page exists
  if (pageIndex >= newTemplate.schemas.length) {
    toast.error(`Page ${pageIndex + 1} doesn't exist. Current pages: ${newTemplate.schemas.length}`);
    return;
  }

  // Ask user if they want to replace or merge with existing content
  const currentFieldsCount = newTemplate.schemas[pageIndex].length;
  let shouldReplace = true;
  
  if (currentFieldsCount > 0) {
    shouldReplace = confirm(
      `Page ${pageIndex + 1} has ${currentFieldsCount} existing fields.\n\n` +
      `Click OK to REPLACE them, or Cancel to ADD new fields alongside existing ones.`
    );
  }

  if (shouldReplace) {
    // Replace the entire page schema - keep original JSON structure intact
    newTemplate.schemas[pageIndex] = cloneDeep(templateSchema);
  } else {
    // Merge with existing fields, ensuring unique names
    const existingNames = new Set(newTemplate.schemas[pageIndex].map(field => field.name));
    const newFields = templateSchema.map(field => {
      const originalField = cloneDeep(field); // Preserve original field structure
      let uniqueName = field.name;
      let counter = 1;
      while (existingNames.has(uniqueName)) {
        uniqueName = `${field.name}_page${pageIndex + 1}_${counter}`;
        counter++;
      }
      existingNames.add(uniqueName);
      return { ...originalField, name: uniqueName }; // Only change the name, keep everything else
    });
    
    newTemplate.schemas[pageIndex] = [...newTemplate.schemas[pageIndex], ...newFields];
  }

  designer.current.updateTemplate(newTemplate);
  
  const action = shouldReplace ? 'Replaced' : 'Added';
  const finalCount = newTemplate.schemas[pageIndex].length;
  toast.success(`✅ ${action} page ${pageIndex + 1} with ${templateSchema.length} fields! Total fields on page: ${finalCount}`);
  
  // Save to localStorage
  localStorage.setItem("template", JSON.stringify(newTemplate));
};

// NEW: Handle page-specific template file loading
const handlePageSpecificTemplateFile = (file: File, pageIndex: number) => {
  if (!file.name.toLowerCase().endsWith('.json')) {
    toast.error('Please select a valid JSON template file');
    return;
  }

  readFile(file, "text").then(async (fileContent) => {
    try {
      const templateData = JSON.parse(fileContent as string);
      
      let schemaToLoad: Schema[] = [];
      let templateType = '';

      // Handle different template formats while preserving JSON structure
      if (Array.isArray(templateData)) {
        // Direct schema array - preserve as-is
        schemaToLoad = templateData as Schema[];
        templateType = 'schema array';
      } else if (templateData.schemas && Array.isArray(templateData.schemas)) {
        // Full template - use first page schema, preserve structure
        if (templateData.schemas.length === 0) {
          toast.warning('Template has no pages/schemas');
          return;
        }
        schemaToLoad = templateData.schemas[0] as Schema[]; // Load first page
        templateType = `template (using page 1 of ${templateData.schemas.length})`;
      } else if (templateData.basePdf?.staticSchema) {
        // Static schema (header/footer) - preserve structure
        schemaToLoad = templateData.basePdf.staticSchema as Schema[];
        templateType = 'static schema (header/footer)';
      } else if (templateData.staticSchema) {
        // Root level staticSchema - preserve structure
        schemaToLoad = templateData.staticSchema as Schema[];
        templateType = 'root staticSchema';
      } else {
        // Try to validate as full template
        try {
          checkTemplate(templateData);
          toast.warning('Valid template found but no compatible schema detected');
          return;
        } catch (templateError) {
          throw new Error('No compatible schema found. Expected: schema array, full template, or static schema.');
        }
      }

      if (!Array.isArray(schemaToLoad)) {
        throw new Error('Schema must be an array');
      }

      if (schemaToLoad.length === 0) {
        toast.warning(`Template loaded but contains no fields in ${templateType}`);
        return;
      }

      // Load the schema to the specific page - preserves original JSON structure
      loadTemplateToPage(pageIndex, schemaToLoad);
      
      toast.info(`📄 Loaded ${schemaToLoad.length} fields from ${templateType}`);

    } catch (error) {
      console.error('Error loading page-specific template:', error);
      const errorMessage = error instanceof Error ? error.message : 'Invalid file format';
      toast.error(`Failed to load template: ${errorMessage}`);
    }
  }).catch((error) => {
    console.error('Error reading file:', error);
    toast.error('Failed to read template file');
  });
};

// NEW: Page-specific template file input handler
const loadPageSpecificTemplate = (pageIndex: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
  if (!e.target.files?.[0]) return;
  
  handlePageSpecificTemplateFile(e.target.files[0], pageIndex);
  
  // Reset the input
  e.target.value = '';
};

// NEW: Get page selector dropdown
const getPageSelector = () => {
  const pages = Array.from({ length: totalPages }, (_, i) => i);
  
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-gray-500">Load to page:</span>
      <select
        disabled={editingStaticSchemas}
        className={`text-xs border rounded px-1 py-1 bg-white ${
          editingStaticSchemas ? "opacity-50 cursor-not-allowed" : "hover:border-gray-400"
        }`}
        onChange={(e) => {
          const pageIndex = parseInt(e.target.value);
          if (isNaN(pageIndex)) return;
          
          const fileInput = document.createElement('input');
          fileInput.type = 'file';
          fileInput.accept = 'application/json,.json';
          fileInput.onchange = (ev) => {
            loadPageSpecificTemplate(pageIndex)(ev as unknown as React.ChangeEvent<HTMLInputElement>);
          };
          fileInput.click();
        }}
      >
        <option value="">Select Page</option>
        {pages.map(pageIndex => (
          <option key={pageIndex} value={pageIndex}>
            Page {pageIndex + 1}
          </option>
        ))}
      </select>
    </div>
  );
};

  const getCurrentPageInfo = () => {
    if (designer.current) {
      const template = designer.current.getTemplate();
      return {
        currentPage: 1, // Since we're not tracking current page navigation
        totalPages: template.schemas.length,
        fieldsOnCurrentPage: template.schemas[0]?.length || 0 // Show first page field count
      };
    }
    return { currentPage: 1, totalPages: 1, fieldsOnCurrentPage: 0 };
  };

  // Header/Footer Functions
  const hasStaticSchema = (): boolean => {
    if (!designer.current) return false;
    const template = designer.current.getTemplate();
    const basePdf = template.basePdf;
    
    // Type guard to check if basePdf has staticSchema property
    if (!isBlankPdf(basePdf)) return false;
    
    return Boolean(basePdf.staticSchema && basePdf.staticSchema.length > 0);
  };

  const getStaticSchemaCount = (): number => {
    if (!designer.current) return 0;
    const template = designer.current.getTemplate();
    const basePdf = template.basePdf;
    
    if (!isBlankPdf(basePdf) || !basePdf.staticSchema) return 0;
    
    return basePdf.staticSchema.length;
  };

  const toggleEditingStaticSchemas = () => {
    if (!designer.current) return;

    if (!editingStaticSchemas) {
      const currentTemplate = cloneDeep(designer.current.getTemplate());
      
      if (!isBlankPdf(currentTemplate.basePdf)) {
        toast.error("Headers/footers can only be added to blank templates. Upload a blank PDF or create a new template first.");
        return;
      }

      setOriginalTemplate(currentTemplate);
      const { width, height } = currentTemplate.basePdf;
      const staticSchema = currentTemplate.basePdf.staticSchema || [];
      
      designer.current.updateTemplate({
        ...currentTemplate,
        schemas: [staticSchema],
        basePdf: { width, height, padding: [0, 0, 0, 0] },
      });

      setEditingStaticSchemas(true);
      toast.success("✨ Now editing headers & footers! Elements you add here will appear on ALL pages.");

    } else {
      const editedTemplate = designer.current.getTemplate();
      if (!originalTemplate) return;
      
      const merged = cloneDeep(originalTemplate);
      if (!isBlankPdf(merged.basePdf)) {
        toast.error("Invalid basePdf format");
        return;
      }

      merged.basePdf.staticSchema = editedTemplate.schemas[0];
      designer.current.updateTemplate(merged);

      // Save the merged template automatically
      localStorage.setItem("template", JSON.stringify(merged));

      setOriginalTemplate(null);
      setEditingStaticSchemas(false);
      setTotalPages(merged.schemas.length);
      
      const elementsCount = editedTemplate.schemas[0]?.length || 0;
      toast.success(`✅ Headers/footers saved! ${elementsCount} elements will appear on all pages.`);
    }
  };

  const addQuickHeader = () => {
    if (!designer.current || !editingStaticSchemas) {
      toast.error("Please start editing headers/footers first");
      return;
    }

    const currentTemplate = designer.current.getTemplate();
    const newTemplate = cloneDeep(currentTemplate);
    
    const headerField = {
      name: `header_${Date.now()}`,
      type: 'text',
      position: { x: 20, y: 15 },
      width: 170,
      height: 10,
      fontSize: 12,
      fontColor: '#000000',
      backgroundColor: '',
      alignment: 'left',
      fontName: 'NotoSerifJP-Regular',
      content: 'Header Text'
    };

    newTemplate.schemas[0].push(headerField);
    designer.current.updateTemplate(newTemplate);
    toast.success("Added header text field!");
  };

  const addQuickFooter = () => {
    if (!designer.current || !editingStaticSchemas) {
      toast.error("Please start editing headers/footers first");
      return;
    }

    const currentTemplate = designer.current.getTemplate();
    const newTemplate = cloneDeep(currentTemplate);
    
    const footerField = {
      name: `footer_${Date.now()}`,
      type: 'text',
      position: { x: 95, y: 285 },
      width: 20,
      height: 8,
      fontSize: 10,
      fontColor: '#666666',
      backgroundColor: '',
      alignment: 'center',
      fontName: 'NotoSerifJP-Regular',
      content: 'Page {pageNumber}'
    };

    newTemplate.schemas[0].push(footerField);
    designer.current.updateTemplate(newTemplate);
    toast.success("Added footer field!");
  };

  const clearHeadersFooters = () => {
    if (!designer.current || !editingStaticSchemas) {
      toast.error("Please start editing headers/footers first");
      return;
    }

    if (confirm("Remove all headers and footers?")) {
      const currentTemplate = designer.current.getTemplate();
      const newTemplate = cloneDeep(currentTemplate);
      newTemplate.schemas[0] = [];
      designer.current.updateTemplate(newTemplate);
      toast.success("All headers/footers removed!");
    }
  };

  // Header/Footer Template Loading (shared logic for file input and drag-drop)
  const handleHeaderFooterTemplateFile = (file: File) => {
    if (!designer.current || !editingStaticSchemas) {
      toast.error("Please start editing headers/footers first");
      return;
    }

    // Check file type
    if (!file.name.toLowerCase().endsWith('.json')) {
      toast.error('Please select a valid JSON template file');
      return;
    }

    readFile(file, "text").then(async (fileContent) => {
      try {
        const templateData = JSON.parse(fileContent as string);
        
        // Validate template structure using checkTemplate if it's a full template
        let headerFooterSchema = [];
        let templateType = '';

        // Handle different template formats (same as handleLoadTemplate logic)
        if (Array.isArray(templateData)) {
          // If it's directly an array of schema elements
          headerFooterSchema = templateData;
          templateType = 'schema array';
        } else if (templateData.basePdf?.staticSchema) {
          // If it's a full template with staticSchema
          headerFooterSchema = templateData.basePdf.staticSchema;
          templateType = 'full template with staticSchema';
        } else if (templateData.schemas && Array.isArray(templateData.schemas[0])) {
          // If it's a template where first schema contains header/footer elements
          headerFooterSchema = templateData.schemas[0];
          templateType = 'template with first schema';
        } else if (templateData.staticSchema) {
          // If it has staticSchema at root level
          headerFooterSchema = templateData.staticSchema;
          templateType = 'root staticSchema';
        } else {
          // Try to validate as full template first
          try {
            checkTemplate(templateData);
            // If it's a valid template but no staticSchema, inform user
            toast.warning('Valid template found but no header/footer elements detected');
            return;
          } catch (templateError) {
            throw new Error('No header/footer elements found in template. Expected formats: staticSchema, full template, or schema array.');
          }
        }

        if (!Array.isArray(headerFooterSchema)) {
          throw new Error('Header/footer schema must be an array');
        }

        if (headerFooterSchema.length === 0) {
          toast.warning('Template loaded but contains no header/footer elements');
          return;
        }

        // Apply the header/footer elements to current template
        const currentTemplate = designer.current!.getTemplate();
        const newTemplate = cloneDeep(currentTemplate);
        
        // Merge with existing elements or replace them
        const currentElementCount = newTemplate.schemas[0]?.length || 0;
        const shouldReplace = currentElementCount > 0 ? confirm(
          `Found ${headerFooterSchema.length} header/footer elements in ${templateType}.\n\n` +
          `Current template has ${currentElementCount} elements.\n\n` +
          `Click OK to REPLACE current elements, or Cancel to ADD to existing elements.`
        ) : true;

        if (shouldReplace) {
          newTemplate.schemas[0] = cloneDeep(headerFooterSchema);
        } else {
          // Add to existing, but ensure unique names
          const existingNames = new Set((newTemplate.schemas[0] || []).map((field) => field.name));
          const newElements = headerFooterSchema.map((field) => {
            let uniqueName = field.name;
            let counter = 1;
            while (existingNames.has(uniqueName)) {
              uniqueName = `${field.name}_${counter}`;
              counter++;
            }
            existingNames.add(uniqueName);
            return { ...field, name: uniqueName };
          });
          
          newTemplate.schemas[0] = [...(newTemplate.schemas[0] || []), ...newElements];
        }

        designer.current!.updateTemplate(newTemplate);
        
        const action = shouldReplace ? 'Replaced with' : 'Added';
        toast.success(
          `✅ ${action} ${headerFooterSchema.length} header/footer elements from ${templateType}!`
        );

      } catch (error) {
        console.error('Error loading header/footer template:', error);
        const errorMessage = error instanceof Error ? error.message : 'Invalid file format';
        toast.error(`Failed to load header/footer template: ${errorMessage}`);
      }
    }).catch((error) => {
      console.error('Error reading file:', error);
      toast.error('Failed to read template file');
    });
  };

  // Load Header/Footer Template from File Input
  const loadHeaderFooterTemplate = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    
    handleHeaderFooterTemplateFile(e.target.files[0]);
    
    // Reset the input so the same file can be selected again
    e.target.value = '';
  };

  // Drag and Drop Handler for Header/Footer Templates
  const [headerFooterDragOver, setHeaderFooterDragOver] = useState(false);

  const handleHeaderFooterDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (editingStaticSchemas) {
      setHeaderFooterDragOver(true);
    }
  };

  const handleHeaderFooterDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setHeaderFooterDragOver(false);
  };

  const handleHeaderFooterDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setHeaderFooterDragOver(false);

    if (!editingStaticSchemas) {
      toast.error("Please start editing headers/footers first");
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    const jsonFile = files.find(file => file.name.toLowerCase().endsWith('.json'));
    
    if (!jsonFile) {
      toast.error('Please drop a valid JSON template file');
      return;
    }

    if (files.length > 1) {
      toast.warning('Multiple files detected. Using the first JSON file found.');
    }

    handleHeaderFooterTemplateFile(jsonFile);
  };

  // Save Header/Footer Template Function
  const saveHeaderFooterTemplate = () => {
    if (!designer.current || !editingStaticSchemas) {
      toast.error("Please start editing headers/footers first");
      return;
    }

    const currentTemplate = designer.current.getTemplate();
    const staticSchema = currentTemplate.schemas[0] || [];
    
    if (staticSchema.length === 0) {
      toast.error("No header/footer elements to save");
      return;
    }

    // Create a template containing only the header/footer elements
    const headerFooterTemplate = {
      staticSchema: cloneDeep(staticSchema),
      metadata: {
        type: 'header-footer-template',
        elements: staticSchema.length,
        createdAt: new Date().toISOString(),
        description: 'Header and footer elements template'
      }
    };

    // Download as JSON file
    const dataStr = JSON.stringify(headerFooterTemplate, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `header-footer-template-${new Date().getTime()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    toast.success(`💾 Saved ${staticSchema.length} header/footer elements as template!`);
  };

  // File Operations - FIXED
  const onChangeBasePDF = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      readFile(e.target.files[0], "dataURL").then(async (basePdf) => {
        if (designer.current) {
          const newTemplate = cloneDeep(designer.current.getTemplate());
          newTemplate.basePdf = basePdf;
          designer.current.updateTemplate(newTemplate);
        }
      });
    }
  };

  const onDownloadTemplate = () => {
    if (designer.current) {
      const templateToDownload = ensureHeaderFooterFieldsAreMerged();
      if (templateToDownload) {
        downloadJsonFile(templateToDownload, "template");
        toast.success("Template downloaded successfully! ❤️");
      } else {
        toast.error("Failed to prepare template for download");
      }
    }
  };

  const onSaveTemplate = (template?: Template) => {
    if (designer.current) {
      const templateToSave = template || ensureHeaderFooterFieldsAreMerged() || designer.current.getTemplate();
      localStorage.setItem("template", JSON.stringify(templateToSave));
      setTotalPages(templateToSave.schemas.length);
      toast.success("Saved on local storage");
    }
  };

  const onResetTemplate = () => {
    localStorage.removeItem("template");
    if (designer.current) {
      const blankTemplate = getBlankTemplate();
      designer.current.updateTemplate(blankTemplate);
      setTotalPages(1);
    }
  };

  // Initialize designer on mount
  useEffect(() => {
    if (designerRef.current) {
      buildDesigner();
    }
    return () => {
      designer.current?.destroy();
    };
  }, [designerRef, buildDesigner]);

  // Navigation Items Configuration
  const navItems: NavItem[] = [
    // Language Selection
    {
      label: "Language",
      content: (
        <select
          disabled={editingStaticSchemas}
          className={`w-24 text-xs border rounded px-2 py-1 bg-white ${
            editingStaticSchemas ? "opacity-50 cursor-not-allowed" : "hover:border-gray-400"
          }`}
          onChange={(e) => designer.current?.updateOptions({ lang: e.target.value as Lang })}
        >
          {translations.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      ),
    },

    // Headers/Footers Section - ENHANCED
    {
      label: editingStaticSchemas ? "🎨 Editing Headers/Footers" : "Headers & Footers",
      content: (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">
            ({editingStaticSchemas ? getStaticSchemaCount() : hasStaticSchema() ? getStaticSchemaCount() : '0'} elements)
          </span>
          
          <button
            className={`px-3 py-1 text-xs border rounded-md font-medium transition-colors ${
              editingStaticSchemas 
                ? "bg-orange-500 hover:bg-orange-600 text-white border-orange-500" 
                : hasStaticSchema()
                  ? "bg-green-500 hover:bg-green-600 text-white border-green-500"
                  : "bg-blue-500 hover:bg-blue-600 text-white border-blue-500"
            }`}
            onClick={toggleEditingStaticSchemas}
            title={editingStaticSchemas ? "Finish editing headers/footers" : "Start editing headers/footers"}
          >
            {editingStaticSchemas ? "✅ Finish" : hasStaticSchema() ? "✏️ Edit" : "➕ Add"}
          </button>
          
          {editingStaticSchemas && (
            <div className="flex items-center gap-1 ml-2 pl-2 border-l border-gray-300">
              <button
                className="px-2 py-1 text-xs border rounded bg-gray-100 hover:bg-gray-200 text-gray-700"
                onClick={addQuickHeader}
                title="Add header text"
              >
                📄 Header
              </button>
              <button
                className="px-2 py-1 text-xs border rounded bg-gray-100 hover:bg-gray-200 text-gray-700"
                onClick={addQuickFooter}
                title="Add footer text"
              >
                🔢 Footer
              </button>
              <button
                className="px-2 py-1 text-xs border rounded bg-red-100 hover:bg-red-200 text-red-600"
                onClick={clearHeadersFooters}
                title="Clear all"
              >
                🗑️ Clear
              </button>
            </div>
          )}

          {/* Header/Footer Template Actions with Drag & Drop */}
          {editingStaticSchemas && (
            <div 
              className={`flex items-center gap-1 ml-2 pl-2 border-l border-gray-300 relative ${
                headerFooterDragOver ? 'bg-blue-50 border-blue-300 rounded-md' : ''
              }`}
              onDragOver={handleHeaderFooterDragOver}
              onDragLeave={handleHeaderFooterDragLeave}
              onDrop={handleHeaderFooterDrop}
            >
              {headerFooterDragOver && (
                <div className="absolute inset-0 border-2 border-dashed border-blue-400 bg-blue-50/50 rounded-md flex items-center justify-center z-10">
                  <span className="text-blue-600 text-xs font-medium">Drop H/F Template</span>
                </div>
              )}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={loadHeaderFooterTemplate}
                />
                <span className="px-2 py-1 text-xs border rounded bg-blue-100 hover:bg-blue-200 text-blue-700 inline-block">
                  📁 Load H/F
                </span>
              </label>
              <button
                className="px-2 py-1 text-xs border rounded bg-green-100 hover:bg-green-200 text-green-700"
                onClick={saveHeaderFooterTemplate}
                title="Save current headers/footers as template"
              >
                💾 Save H/F
              </button>
            </div>
          )}
        </div>
      ),
    },

    // Page Management - ENHANCED with Page-Specific Template Loading
    {
      label: "Pages",
      content: (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-600">
            {totalPages} pages | Page {getCurrentPageInfo().currentPage} | {getCurrentPageInfo().fieldsOnCurrentPage} fields
          </span>
          
          {/* Standard Page Actions */}
          <div className="flex gap-1 ml-2 pl-2 border-l border-gray-300">
            <button
              className="px-2 py-1 text-xs border rounded bg-green-100 hover:bg-green-200 text-green-700"
              onClick={addNewPage}
              disabled={editingStaticSchemas}
              title="Add page"
            >
              ➕
            </button>
            <button
              className="px-2 py-1 text-xs border rounded bg-blue-100 hover:bg-blue-200 text-blue-700"
              onClick={loadSampleMultiPageTemplate}
              disabled={editingStaticSchemas}
              title="Load sample"
            >
              📄
            </button>
            <button
              className="px-2 py-1 text-xs border rounded bg-yellow-100 hover:bg-yellow-200 text-yellow-700"
              onClick={() => {
                const pageNum = prompt(`Duplicate page (1-${totalPages}):`);
                if (pageNum) {
                  const index = parseInt(pageNum) - 1;
                  if (index >= 0 && index < totalPages) {
                    duplicatePage(index);
                  }
                }
              }}
              disabled={editingStaticSchemas}
              title="Duplicate page"
            >
              📑
            </button>
            <button
              className="px-2 py-1 text-xs border rounded bg-red-100 hover:bg-red-200 text-red-700"
              onClick={() => {
                const pageNum = prompt(`Remove page (1-${totalPages}):`);
                if (pageNum) {
                  const index = parseInt(pageNum) - 1;
                  if (index >= 0 && index < totalPages && totalPages > 1) {
                    if (confirm(`Remove page ${index + 1}?`)) {
                      removePage(index);
                    }
                  }
                }
              }}
              disabled={editingStaticSchemas || totalPages === 1}
              title="Remove page"
            >
              🗑️
            </button>
          </div>

          {/* Page-Specific Template Loading */}
          <div className="flex items-center gap-1 ml-2 pl-2 border-l border-gray-300">
            {getPageSelector()}
            <span className="text-xs text-gray-400">|</span>
            <div className="flex gap-1">
              {Array.from({ length: Math.min(totalPages, 6) }, (_, i) => (
                <label key={i} className="cursor-pointer">
                  <input
                    type="file"
                    accept="application/json,.json"
                    className="hidden"
                    onChange={loadPageSpecificTemplate(i)}
                    disabled={editingStaticSchemas}
                  />
                  <span 
                    className={`px-2 py-1 text-xs border rounded transition-colors ${
                      editingStaticSchemas 
                        ? "opacity-50 cursor-not-allowed bg-gray-100 text-gray-400" 
                        : "cursor-pointer bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
                    }`}
                    title={`Load template to page ${i + 1}`}
                  >
                    P{i + 1}
                  </span>
                </label>
              ))}
              {totalPages > 6 && (
                <span className="text-xs text-gray-400 px-1">...</span>
              )}
            </div>
          </div>
        </div>
      ),
    },

    // File Operations
    {
      label: "PDF & Template",
      content: (
        <div className="flex items-center gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500">Base PDF</label>
            <input
              disabled={editingStaticSchemas}
              type="file"
              accept="application/pdf"
              className={`text-xs border rounded px-2 py-1 w-32 ${
                editingStaticSchemas ? "opacity-50 cursor-not-allowed" : "hover:border-gray-400"
              }`}
              onChange={onChangeBasePDF}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500">Load Template</label>
            <input
              disabled={editingStaticSchemas}
              type="file"
              accept="application/json"
              className={`text-xs border rounded px-2 py-1 w-32 ${
                editingStaticSchemas ? "opacity-50 cursor-not-allowed" : "hover:border-gray-400"
              }`}
              onChange={(e) => handleLoadTemplate(e, designer.current)}
            />
          </div>
        </div>
      ),
    },

    // Template Actions
    {
      label: "Template Actions",
      content: (
        <div className="flex items-center gap-2">
          <button
            disabled={editingStaticSchemas}
            className={`px-3 py-1 text-xs border rounded bg-white hover:bg-gray-100 ${
              editingStaticSchemas ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={() => onSaveTemplate()}
          >
            💾 Save Local
          </button>
          <button
            disabled={editingStaticSchemas}
            className={`px-3 py-1 text-xs border rounded bg-white hover:bg-gray-100 ${
              editingStaticSchemas ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={onResetTemplate}
          >
            🔄 Reset
          </button>
        </div>
      ),
    },
    
    // PDF Actions
    {
      label: "PDF Actions",
      content: (
        <div className="flex items-center gap-2">
          <button
            disabled={editingStaticSchemas}
            className={`px-3 py-1 text-xs border rounded bg-blue-500 hover:bg-blue-600 text-white ${
              editingStaticSchemas ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={onPreviewPDF}
            title="Preview PDF with form data"
          >
            🔍 Preview
          </button>
          
          <button
            disabled={editingStaticSchemas}
            className={`px-3 py-1 text-xs border rounded bg-gray-500 hover:bg-gray-600 text-white ${
              editingStaticSchemas ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={onDownloadTemplate}
          >
            💾 Download Template
          </button>
          
          <button
            disabled={editingStaticSchemas}
            className={`px-3 py-1 text-xs border rounded bg-green-500 hover:bg-green-600 text-white ${
              editingStaticSchemas ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={async () => {
              const startTimer = performance.now();
              
              // Ensure header/footer fields are properly merged before generating PDF
              const templateToGenerate = ensureHeaderFooterFieldsAreMerged();
              if (!templateToGenerate) {
                toast.error("Failed to prepare template for PDF generation");
                return;
              }
              
              // Temporarily update the designer with the merged template if needed
              let originalTemplate = null;
              if (editingStaticSchemas) {
                originalTemplate = designer.current!.getTemplate();
                designer.current!.updateTemplate(templateToGenerate);
              }
              
              try {
                await generatePDF(designer.current);
                const endTimer = performance.now();
                toast.info(`Generated PDF in ${Math.round(endTimer - startTimer)}ms ⚡️`);
              } finally {
                // Restore original template if we changed it
                if (originalTemplate) {
                  designer.current!.updateTemplate(originalTemplate);
                }
              }
            }}
          >
            📄 Generate PDF
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      {editingStaticSchemas && (
        <div 
          className={`bg-orange-50 border-b border-orange-200 px-4 py-2 text-sm text-orange-800 ${
            headerFooterDragOver ? 'bg-blue-50 border-blue-200' : ''
          }`}
          onDragOver={handleHeaderFooterDragOver}
          onDragLeave={handleHeaderFooterDragLeave}
          onDrop={handleHeaderFooterDrop}
        >
          <div className="flex items-center justify-between relative">
            {headerFooterDragOver && (
              <div className="absolute inset-0 border-2 border-dashed border-blue-400 bg-blue-50/50 rounded-md flex items-center justify-center z-10">
                <span className="text-blue-600 font-medium">Drop JSON template file to load header/footer elements</span>
              </div>
            )}
            <span>
              🎨 <strong>Header/Footer Mode:</strong> Elements you add here will appear on ALL pages
              {headerFooterDragOver && <span className="ml-2 text-blue-600">(Drop file to load)</span>}
            </span>
            <button
              onClick={toggleEditingStaticSchemas}
              className="px-3 py-1 bg-orange-600 text-white rounded text-xs hover:bg-orange-700 relative z-20"
            >
              ✅ Finish Editing
            </button>
          </div>
        </div>
      )}
      
      <EnhancedNavBar items={navItems} />
      <div ref={designerRef} className="flex-1 w-full" />
    </>
  );
}

export default DesignerApp;